import SeoHead from "@/components/SeoHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Politique de Confidentialité | Carreaux-Bat Valais — RGPD"
        description="Politique de confidentialité et de protection des données personnelles de Carreaux-Bat Valais. Conforme au RGPD et à la loi suisse sur la protection des données (LPD)."
        canonical="https://carreaux-bat.ch/privacy"
        ogTitle="Politique de Confidentialité — Carreaux-Bat Valais"
        ogDescription="Politique de confidentialité et de protection des données personnelles de Carreaux-Bat Valais."
      />
      <Navbar />

      <section className="relative pt-32 pb-20">
        <img
          src="/images/privacy-hero.jpg"
          alt="Politique de confidentialité et sécurité des données"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-2 text-sm text-white/60 mb-4">
            <Link to="/" className="hover:text-amber-400 transition-colors">Accueil</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white/80">Politique de confidentialité</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Politique de confidentialité</h1>
          <p className="text-white/60 text-sm">Dernière mise à jour : 7 mai 2026</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 space-y-12">
          <div>
            <h2 className="text-xl font-bold text-stone-900 mb-3">1. Qui sommes-nous ?</h2>
            <p className="text-stone-600 text-sm leading-relaxed">
              Carreaux-Bat Valais, artisan carreleur basé à Grône (Valais, Suisse). Notre adresse : Rue de Pramagnon 32, 3979 Grône. Email : info@carreaux-bat.ch. Téléphone : +41 77 511 75 52. Nous sommes responsables du traitement de vos données personnelles conformément à la loi suisse sur la protection des données (LPD) et au Règlement général sur la protection des données (RGPD).
            </p>
          </div>

          <div>
            <h2 className="text-xl font-bold text-stone-900 mb-3">2. Quelles données collectons-nous ?</h2>
            <p className="text-stone-600 text-sm leading-relaxed mb-3">
              Nous collectons uniquement les données nécessaires pour vous contacter et traiter votre demande de devis :
            </p>
            <ul className="list-disc list-inside text-stone-600 text-sm leading-relaxed space-y-1">
              <li>Nom et prénom</li>
              <li>Adresse email</li>
              <li>Numéro de téléphone</li>
              <li>Adresse postale (rue, NPA, ville)</li>
              <li>Type de service demandé</li>
              <li>Message / description du projet</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-bold text-stone-900 mb-3">3. Pourquoi collectons-nous ces données ?</h2>
            <p className="text-stone-600 text-sm leading-relaxed">
              Nous utilisons vos données personnelles uniquement pour :
            </p>
            <ul className="list-disc list-inside text-stone-600 text-sm leading-relaxed space-y-1 mt-2">
              <li>Vous recontacter suite à votre demande de devis ou de renseignements</li>
              <li>Préparer une estimation personnalisée pour votre projet de carrelage ou rénovation</li>
              <li>Vous informer sur l'avancement de votre demande</li>
            </ul>
            <p className="text-stone-600 text-sm leading-relaxed mt-3">
              Nous ne vendons, ne louons et ne transmettons jamais vos données à des tiers à des fins commerciales.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-bold text-stone-900 mb-3">4. Cookies et traceurs</h2>
            <p className="text-stone-600 text-sm leading-relaxed">
              Notre site utilise des cookies strictement nécessaires au bon fonctionnement du site (préférences de langue, consentement cookies). Nous n'utilisons pas de cookies de traçage publicitaire ou d'analyse comportementale (Google Analytics, Facebook Pixel, etc.) sans votre consentement explicite.
            </p>
            <p className="text-stone-600 text-sm leading-relaxed mt-3">
              Lorsque vous visitez le site, un bandeau vous permet de gérer vos préférences en matière de cookies. Vous pouvez modifier ces préférences à tout moment en cliquant sur "Gérer les cookies" en bas de page.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-bold text-stone-900 mb-3">5. Durée de conservation</h2>
            <p className="text-stone-600 text-sm leading-relaxed">
              Nous conservons vos données personnelles pendant un maximum de 24 mois après votre dernier contact. Passé ce délai, vos données sont supprimées de nos systèmes. Si vous souhaitez une suppression anticipée, contactez-nous à info@carreaux-bat.ch.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-bold text-stone-900 mb-3">6. Vos droits</h2>
            <p className="text-stone-600 text-sm leading-relaxed mb-3">
              Conformément au RGPD et à la loi suisse sur la protection des données, vous disposez des droits suivants :
            </p>
            <ul className="list-disc list-inside text-stone-600 text-sm leading-relaxed space-y-1">
              <li><strong>Droit d'accès</strong> : obtenir une copie de vos données personnelles</li>
              <li><strong>Droit de rectification</strong> : corriger des données inexactes</li>
              <li><strong>Droit à l'effacement</strong> (« droit à l'oubli ») : demander la suppression de vos données</li>
              <li><strong>Droit à la limitation du traitement</strong> : suspendre l'utilisation de vos données</li>
              <li><strong>Droit d'opposition</strong> : vous opposer au traitement de vos données</li>
              <li><strong>Droit à la portabilité</strong> : recevoir vos données dans un format structuré</li>
            </ul>
            <p className="text-stone-600 text-sm leading-relaxed mt-3">
              Pour exercer ces droits, envoyez-nous un email à <a href="mailto:info@carreaux-bat.ch" className="text-amber-700 hover:text-amber-800 transition-colors">info@carreaux-bat.ch</a>. Nous vous répondrons dans un délai maximum de 30 jours.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-bold text-stone-900 mb-3">7. Sécurité des données</h2>
            <p className="text-stone-600 text-sm leading-relaxed">
              Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données personnelles contre la perte, l'accès non autorisé, la modification ou la divulgation. Les données transmises via notre formulaire de contact sont chiffrées en transit (HTTPS).
            </p>
          </div>

          <div>
            <h2 className="text-xl font-bold text-stone-900 mb-3">8. Hébergement et sous-traitants</h2>
            <p className="text-stone-600 text-sm leading-relaxed">
              Notre site est hébergé via Readdy.ai. Les données collectées via le formulaire de contact sont transmises et stockées via les services de Readdy. Nous ne faisons appel à aucun autre sous-traitant pour le traitement de vos données personnelles.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-bold text-stone-900 mb-3">9. Réclamation</h2>
            <p className="text-stone-600 text-sm leading-relaxed">
              Si vous estimez que le traitement de vos données personnelles viole la législation applicable, vous avez le droit d'introduire une réclamation auprès de l'<a href="https://www.edoeb.admin.ch/" target="_blank" rel="nofollow" className="text-amber-700 hover:text-amber-800 transition-colors">Autorité fédérale de protection des données (PFPDT)</a> en Suisse, ou auprès de l'autorité de contrôle compétente de votre pays de résidence dans l'UE.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-bold text-stone-900 mb-3">10. Modifications de cette politique</h2>
            <p className="text-stone-600 text-sm leading-relaxed">
              Nous pouvons mettre à jour cette politique de confidentialité à tout moment. La date de dernière mise à jour est indiquée en haut de cette page. Nous vous encourageons à consulter régulièrement cette page pour prendre connaissance des éventuelles modifications.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-bold text-stone-900 mb-3">11. Contact</h2>
            <p className="text-stone-600 text-sm leading-relaxed">
              Pour toute question concernant cette politique de confidentialité ou le traitement de vos données, contactez-nous :
            </p>
            <ul className="list-disc list-inside text-stone-600 text-sm leading-relaxed space-y-1 mt-2">
              <li>Email : <a href="mailto:info@carreaux-bat.ch" className="text-amber-700 hover:text-amber-800 transition-colors">info@carreaux-bat.ch</a></li>
              <li>Téléphone : <a href="tel:+41775117552" className="text-amber-700 hover:text-amber-800 transition-colors">+41 77 511 75 52</a></li>
              <li>Adresse : Rue de Pramagnon 32, 3979 Grône, Valais, Suisse</li>
            </ul>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}